import { toPriceText } from './catalogCard'
import type { PresentationContext } from './catalogCard'

/** One cart line, narrowed to what a total is made of. `CartLineInput` fits. */
export interface CheckoutLineInput {
  name: string
  quantity: number
  lineTotal: number
}

export interface CheckoutSummaryInput {
  lines: readonly CheckoutLineInput[] | null | undefined
  /**
   * The backend's total for the goods. **Not recomputed here** — promotions,
   * fees and rounding are decided server-side, and a second opinion rendered
   * next to the real one is just a way to be wrong.
   */
  cartTotal: number
  /** Host-supplied, and today local-only: the chosen shipping method's fee. */
  shippingFee?: number
  /** The customer's credit line with this distributor. */
  creditAvailable?: number
}

/**
 * Whether this order can go on the distributor's credit.
 *
 * Three states rather than a boolean, because "we cannot tell" is not "it
 * fits". When prices are hidden for a customer the totals are not renderable
 * and a `total > credit` comparison would silently pass, quietly offering
 * credit for an order nobody can price.
 */
export type CreditCheck = 'ok' | 'exceeded' | 'unavailable'

export interface CheckoutOrderLine {
  name: string
  quantity: number
  amount: number
}

export interface CheckoutSummary {
  cartTotal: number
  shipping: number
  total: number
  cartTotalText: string
  shippingText: string
  totalText: string
  /** Units, not lines — two of a product is two items. */
  itemCount: number
  creditAvailable: number
  /** Negative when the order does not fit. */
  creditRemaining: number
  creditCheck: CreditCheck
  /**
   * Prices cannot be shown to this customer. The numbers above are still real
   * — the screen needs them to decide things — but their `*Text` forms are
   * empty, and a screen must render an explanation rather than a blank total.
   */
  pricesHidden: boolean
  orderLines: CheckoutOrderLine[]
}

/**
 * Money in floats drifts: `0.1 + 0.2` is not `0.3`, and a total of
 * `8500.000000001` compared against a credit line of `8500` would refuse a
 * payment method for a rounding error nobody can see. Every amount is settled
 * to cents before it is compared or formatted.
 */
const cents = (amount: number): number => Math.round(amount * 100) / 100

/**
 * Everything the delivery and payment screens need to show a total and decide
 * whether it can go on credit.
 *
 * The seam between real and local stays at the call site, which is the point:
 * `cartTotal` is the backend's and `shippingFee` is the host's, and this
 * function does not know or care which is which. When commerce-scs grows
 * shipping, only the caller changes.
 *
 * It used to take a `couponRate` and a `couponBase` too, for a coupon that was
 * a fixture — there is no coupon endpoint in commerce-scs, and none in cinnamon
 * either. A discount the client invents is money on screen that no server will
 * honour, so both are gone rather than kept warm for an API that does not
 * exist. See `docs/NOT_PORTED.md`.
 */
export const toCheckoutSummary = (
  { lines, cartTotal, shippingFee = 0, creditAvailable = 0 }: CheckoutSummaryInput,
  ctx: PresentationContext,
): CheckoutSummary => {
  const goods = cents(cartTotal)
  const shipping = cents(Math.max(0, shippingFee))
  const total = cents(goods + shipping)

  const credit = cents(creditAvailable)
  const creditRemaining = cents(credit - total)
  const creditCheck: CreditCheck = ctx.hidePrices
    ? 'unavailable'
    : total > credit
      ? 'exceeded'
      : 'ok'

  const orderLines = (lines ?? []).map((line) => ({
    name: line.name,
    quantity: line.quantity,
    amount: cents(line.lineTotal),
  }))

  return {
    cartTotal: goods,
    shipping,
    total,
    cartTotalText: toPriceText(goods, ctx),
    shippingText: toPriceText(shipping, ctx),
    totalText: toPriceText(total, ctx),
    itemCount: orderLines.reduce((sum, line) => sum + line.quantity, 0),
    creditAvailable: credit,
    creditRemaining,
    creditCheck,
    pricesHidden: ctx.hidePrices,
    orderLines,
  }
}
